import 'vite/modulepreload-polyfill';
import { datadogLogs } from '@datadog/browser-logs';

const ENVIRONMENTS = window.ALLBIRDS.environment.types;

const DATADOG_LOGGER_SECRETS = (() => {
  const datadog = (window.ALLBIRDS && window.ALLBIRDS.Datadog) || {};
  return {
    [ENVIRONMENTS.DEVELOPMENT]: datadog.devToken || '',
    [ENVIRONMENTS.PRODUCTION]: datadog.prodToken || '',
    [ENVIRONMENTS.STAGING]: datadog.stagingToken || '',
  };
})();

const ENV_MAP = {
  [ENVIRONMENTS.DEVELOPMENT]: 'dev',
  [ENVIRONMENTS.PRODUCTION]: 'prod',
  [ENVIRONMENTS.STAGING]: 'test',
};

const SERVICE = 'allbirds-theme-2.0';
const clientName = 'Allbirds Shopify';

const { env } = window.ALLBIRDS.environment;

let initialized = false;
/**
 *
 * @returns string with a UUID format
 */
const uuidv4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/x|y/g, (match) =>
    ((Math.random() * 16) | (match === 'y' ? 8 : 0)).toString(16),
  );

const init = () => {
  if (!initialized) {
    const geoCode = `${window.Shopify.country}_WEB`;
    const fullShopName = window.Shopify.shop ?? '';
    const shopName = fullShopName.split('.')[0];
    const DEFAULT_ATTRIBUTES = {
      clientName,
      geoCode,
      isHydrogen: false,
      locale: window.Shopify.locale,
      shopName,
    };
    const clientToken = DATADOG_LOGGER_SECRETS[env];
    if (!clientToken) {
      console.error('[Allbirds pixel] - Datadog - No client token found');
      return;
    }
    const envMapping = ENV_MAP[env];
    const pageId = uuidv4();

    try {
      datadogLogs.init({
        beforeSend: (log) => {
          Object.assign(log, { ...DEFAULT_ATTRIBUTES, pageId });
        },
        clientToken,
        env: envMapping,
        forwardErrorsToLogs: true,
        service: SERVICE,
        silentMultipleInit: true, // Prevent logging errors while having multiple init.
      });

      initialized = true;
    } catch (error) {
      console.error('[Allbirds pixel] - Datadog - Error initializing datadog', error);
    }
  }
};

/**
 * This methods logs a message in info level, object and/or exception to the Datadog Log.
 *
 * @param {string} message to log
 * @param {object} context any object with properties to add to the log message
 * @param {Error} error an exception to log
 */
const info = (message, context = {}, error) => {
  try {
    datadogLogs.logger.info(message, context, error);
  } catch (error) {
    console.error('[Allbirds pixel] - Datadog - Error logging info', error);
  } finally {
    console.info(message, context);
  }
};

/**
 * This methods logs a message in warn level, object and/or exception to the Datadog Log and browser console.
 *
 * @param {string} message to log
 * @param {object} context any object with properties to add to the log message
 * @param {Error} error an exception to log
 */
const warn = (message, context = {}, error) => {
  try {
    datadogLogs.logger.warn(message, context, error);
  } catch (error) {
    console.error('[Allbirds pixel] - Datadog - Error logging warn', error);
  } finally {
    console.warn(message, error, context);
  }
};

/**
 * This methods logs a message in error level, object and/or exception to the Datadog Log and browser console.
 *
 * @param {string} message to log
 * @param {object} context any object with properties to add to the log message
 * @param {Error} error an exception to log
 */
const error = (message, context = {}, error) => {
  const errorMessage = message || error?.message || error || 'no error message';
  try {
    datadogLogs.logger.error(errorMessage, context, error);
  } catch (error) {
    console.error('[Allbirds pixel] - Datadog - Error logging error', error);
  } finally {
    console.error(errorMessage, error, context);
  }
};

/**
 * Retrieves the session ID of datadog.
 *
 * @returns {string} the session ID
 */
const getSessionId = () => {
  try {
    const logs = datadogLogs.getInternalContext();
    return logs?.session_id;
  } catch (error) {
    console.error('[Allbirds pixel] - Datadog - Error getting session id', error);

    return null;
  }
};

init();

export { error, getSessionId, info, warn };
